// 열람 카드의 단계 넷, 개시 전, rallying, 행군, 착탄 뒤
//
// ── 왜 셋에서 넷이 됐는가 (2026-10-14 사용자) ─────────────────────────────────
//
// 전에는 개시부터 착탄까지를 한 단계로 묶어 「행군 중」으로 칠했다. 개시 순간 카드가 live 면과 광채를
// 얻고 진행 막대가 개시부터 재기 시작했다. 그런데 개시 뒤 rallying 동안은 아직 아무것도 출발하지
// 않았다. 정본의 rally 상태 절도 marching 으로 넘어가는 시점을 `open + rallying` 경과로 적어 두었다.
// 사용자가 8990 실화면을 보고 「open 이 지난 rally 를 open 전과 구분하되, 그것이 착탄 완료나 비활성은
// 아니다」라고 지시했다. 그래서 개시 뒤를 rallying 과 행군 둘로 가른다.
//
// ── 경계는 어디인가 ──────────────────────────────────────────────────────────
//
// 계산 층(`core/timing/attack.ts`)이 `open = impact − rallying − marching` 으로 개시를 정한다. 그래서
// 행군 출발 시각은 `impact − marching` 이고 `open + rallying` 과 같은 값이다. 착탄 쪽에서 빼는 이유는 화면이
// 고정해서 보여 주는 착탄 시각과 막대의 끝이 한 값에서 나오게 하려는 것이다.
//
// 개시 경계만 방향이 다르다. **개시 그 순간은 아직 개시 전이다.** 신호 존의 큰 숫자가 그 순간을
// 「개시까지 T−0:00.0」으로 그리고 끈 상태 안내도 그 판단을 따르므로, 카드가 같은 순간에 「개시함」을
// 그리면 한 틱 동안 두 자리가 다른 말을 한다(2026-10-14 반증 검토). 그래서 판단을 `openPassed` 하나로
// 두고 카드와 신호 존이 함께 쓴다. 행군 출발과 착탄 그 순간은 다음 단계다(출발 그 순간은 행군 중,
// 착탄 그 순간은 착탄 뒤).
//
// ── 저장하지 않는다 ──────────────────────────────────────────────────────────
//
// 단계는 시각에서 매번 파생한다. CT 가 retime 으로 개시를 미래로 옮기면 다음 렌더에서 곧바로 개시 전으로
// 돌아온다. 단계를 상태로 들고 있으면 그 되돌림을 누군가 따로 챙겨야 한다.

/** 열람 카드가 거치는 단계. 화면에는 문구가 아니라 면과 잉크로만 드러난다. */
export type Phase = 'before' | 'rallying' | 'marching' | 'impacted'

/** 단계를 가르는 데 필요한 시각 셋. 모두 밀리초다. */
export interface PhaseTimes {
  openMs: number
  impactMs: number
  /** 이 rally 를 맡은 사람의 행군 시간(dispatch 때 얼어붙은 값). */
  marchingMs: number
}

/**
 * 행군이 출발하는 시각. `impact − marching` 이고 계산 층의 식으로는 `open + rallying` 과 같다.
 * 값이 어긋난 입력이 와도 개시보다 앞서지 않게 묶는다(그러면 rallying 이 음수 길이가 된다).
 */
export function marchStartOf(times: PhaseTimes): number {
  return Math.max(times.openMs, times.impactMs - times.marchingMs)
}

/**
 * 개시가 지났는가. 개시 그 순간은 아직 지나지 않았다. 카드의 단계와 신호 존의 큰 숫자가 같은 판단을
 * 쓰게 하려고 여기 하나만 둔다.
 */
export function openPassed(openMs: number, nowMs: number): boolean {
  return nowMs > openMs
}

/** 지금이 어느 단계인가. 개시 그 순간은 개시 전이고, 출발과 착탄 그 순간은 다음 단계다. */
export function phaseOf(times: PhaseTimes, nowMs: number): Phase {
  if (!openPassed(times.openMs, nowMs)) return 'before'
  if (nowMs < marchStartOf(times)) return 'rallying'
  if (nowMs < times.impactMs) return 'marching'
  return 'impacted'
}

/**
 * 행군 막대가 얼마나 찼는가(0 이상 1 이하). **행군 구간만 잰다.** 출발 전에는 0, 착탄 뒤에는 1 이다.
 * 행군 길이가 0 이면 채울 구간이 없으므로 1 이다.
 */
export function marchProgress(times: PhaseTimes, nowMs: number): number {
  const start = marchStartOf(times)
  const span = times.impactMs - start
  if (span <= 0) return 1
  return Math.min(1, Math.max(0, (nowMs - start) / span))
}
